import { useState } from 'react';
import * as Yup from 'yup';
import { TextField, Box } from '@mui/material';
import { axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import LoadingButton from '@mui/lab/LoadingButton';

const validationSchema = Yup.object().shape({
  domain: Yup.string()
    .matches(
      /^(localhost(:\d{1,5})?|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,11})$/,
      'Enter a valid domain (e.g. example.com or localhost:5000)'
    )
    .required('Domain is required'),

  success_redirect_url: Yup.string()
    .matches(/^\/?[a-zA-Z0-9-_./]*$/, 'Only relative paths allowed (e.g. /abc, abc, /pqr)')
    .required('Success redirect path is required'),

  failure_redirect_url: Yup.string()
    .matches(/^\/?[a-zA-Z0-9-_./]*$/, 'Only relative paths allowed (e.g. /abc, abc, /pqr)')
    .required('Failure redirect path is required'),
});

export const useCreateConsoleAuthForm = (navigate) => {
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    domain: '',
    success_redirect_url: '',
    failure_redirect_url: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    const trimmedValue = value.trim();

    setFormValues({
      ...formValues,
      [name]: trimmedValue,
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formValues, { abortEarly: false });

      setLoading(true);
      const res = await axiosPostCall('saveConsoleAuth', formValues);
      renderSuccessMessage(res.message || 'OAuth app created successfully');
      navigate('/console_auth');
    } catch (error) {
      if (error.name === 'ValidationError') {
        const fieldErrors = {};
        error.inner.forEach((err) => {
          fieldErrors[err.path] = err.message;
        });
        setErrors(fieldErrors);
        return;
      }
      console.error('Error creating OAuth app:', error);
      renderErrorMessage(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return {
    formValues,
    errors,
    loading,
    handleChange,
    handleSubmit,
  };
};

export const ConsoleAuthForm = ({ formValues, errors, loading, handleChange, handleSubmit }) => {
  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
      <TextField
        fullWidth
        label="Domain"
        name="domain"
        value={formValues.domain}
        onChange={handleChange}
        margin="normal"
        error={!!errors.domain}
        helperText={errors.domain}
      />
      <TextField
        fullWidth
        label="Success Redirect URL"
        name="success_redirect_url"
        value={formValues.success_redirect_url}
        onChange={handleChange}
        margin="normal"
        error={!!errors.success_redirect_url}
        helperText={errors.success_redirect_url}
      />
      <TextField
        fullWidth
        label="Failure Redirect URL"
        name="failure_redirect_url"
        value={formValues.failure_redirect_url}
        onChange={handleChange}
        margin="normal"
        error={!!errors.failure_redirect_url}
        helperText={errors.failure_redirect_url}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <LoadingButton variant="outlined" type="submit" disabled={loading}>
          Create
        </LoadingButton>
      </Box>
    </form>
  );
};
